import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setCsrfToken } from '../api/client';
import type { MeDto } from '../api/types';

interface AuthValue {
  user: MeDto | null;
  /** 'loading' only during the very first session check, so guards don't flash a redirect. */
  status: 'loading' | 'ready';
  login: (identifier: string, password: string) => Promise<void>;
  register: (input: { email: string; username: string; password: string; displayName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  applyUser: (user: MeDto) => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeDto | null>(null);
  const [status, setStatus] = useState<AuthValue['status']>('loading');

  const load = useCallback(async () => {
    try {
      const session = await api.auth.me();
      setCsrfToken(session.csrfToken ?? null);
      setUser(session.user);
    } catch {
      setCsrfToken(null);
      setUser(null);
    } finally {
      setStatus('ready');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const login = useCallback(async (identifier: string, password: string) => {
    const result = await api.auth.login(identifier, password);
    setCsrfToken(result.csrfToken);
    setUser(result.user);
  }, []);

  const register = useCallback<AuthValue['register']>(async (input) => {
    const result = await api.auth.register(input);
    setCsrfToken(result.csrfToken);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setCsrfToken(null);
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user, status, login, register, logout, applyUser: setUser, refresh: load }),
    [user, status, login, register, logout, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
